import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TribeAuthProvider } from "@/context/TribeAuthContext";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import TribeProtectedRoute from "@/components/TribeProtectedRoute";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Mission from "@/pages/Mission";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Vault from "@/pages/Vault";
import CredentialVerification from "@/pages/CredentialVerification";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { PrivacyPolicy, TermsOfService, AccessibilityStatement } from "@/pages/Legal";

import InstitutionHub from "@/pages/InstitutionHub";
import LineageHub from "@/pages/LineageHub";
import MembershipHub from "@/pages/MembershipHub";

import TribalPortal from "@/pages/TribalPortal";
import TribeAuth from "@/pages/TribeAuth";
import TribeDashboard from "@/pages/TribeDashboard";
import { TribeSubscriptionSuccess, TribeSubscriptionCancel } from "@/pages/TribeSubscriptionResult";

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/mission" element={<Mission />} />
          <Route path="/eligibility" element={<LineageHub />} />
          <Route path="/institution" element={<InstitutionHub />} />
          <Route path="/membership" element={<MembershipHub />} />
          <Route path="/tribal-portal" element={<TribalPortal />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/credential-verification" element={<CredentialVerification />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />

          {/* Redirects from legacy routes to consolidated hubs */}
          <Route path="/verify-genealogy" element={<Navigate to="/eligibility?tab=verify" replace />} />
          <Route path="/tribal-structure" element={<Navigate to="/institution?tab=structure" replace />} />
          <Route path="/structure" element={<Navigate to="/institution?tab=philosophy" replace />} />
          <Route path="/benefits" element={<Navigate to="/membership?tab=benefits" replace />} />
          <Route path="/order-id" element={<Navigate to="/membership?tab=order" replace />} />
        </Route>

        <Route path="/login" element={<Login />} />
        <Route path="/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

        {/* Tribal Government Portal */}
        <Route path="/tribes/login" element={<TribeAuth />} />
        <Route path="/tribes/dashboard" element={<TribeProtectedRoute><TribeDashboard /></TribeProtectedRoute>} />
        <Route path="/tribes/subscription/success" element={<TribeProtectedRoute><TribeSubscriptionSuccess /></TribeProtectedRoute>} />
        <Route path="/tribes/subscription/cancel" element={<TribeProtectedRoute><TribeSubscriptionCancel /></TribeProtectedRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <TribeAuthProvider>
            <AppRouter />
            <Toaster />
          </TribeAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
